// Single source of truth for blog taxonomy. Nine canonical categories - one
// clear home for every article, no near-duplicates. Consolidating these also
// gives Google one strong topic cluster per category instead of sixteen thin
// ones. Legacy names are mapped so old feed data and dynamic posts land in the
// right place.

export const BLOG_CATEGORIES = [
  {
    name: 'Neuroscience',
    slug: 'neuroscience',
    emoji: '🧠',
    active: 'from-purple-600 to-indigo-600',
    ring: 'ring-purple-200',
    badge: 'from-purple-500 to-indigo-600',
    blurb: 'How the brain and nervous system actually work',
    heading: 'Neuroscience, made usable',
    intro: 'The brain and nervous system explained at the level of mechanism - what the structures actually do, which chemicals reach which compartment, and why any of it matters to how you feel today. Written for people living it, not for exams.',
    metaTitle: 'Neuroscience Articles - Brain & Nervous System Explained | Recalibrate',
    metaDescription: 'Plain-English neuroscience for people living with long-term symptoms: descending pain modulation, the blood-brain barrier, autonomic states, neuroplasticity, cortisol and the HPA axis, and what each one means in practice.',
  },
  {
    name: 'Pain Science',
    slug: 'pain-science',
    emoji: '🔬',
    active: 'from-rose-500 to-pink-600',
    ring: 'ring-rose-200',
    badge: 'from-rose-500 to-pink-600',
    blurb: 'Mechanisms of persistent pain and sensitisation',
    heading: 'Pain science without the hand-waving',
    intro: 'Why pain stops matching the tissue, how central sensitisation changes the rules in the spinal cord, and what the evidence says about the treatments that actually shift it. Including the parts that explain why your scans came back clear.',
    metaTitle: 'Pain Science Articles - Central Sensitisation & Chronic Pain | Recalibrate',
    metaDescription: 'Evidence-based pain science: central sensitisation, allodynia, descending facilitation, fascia and referred pain, cervicogenic headache, and why pain intensity is not a damage meter.',
  },
  {
    name: 'Psychology & Mind-Body',
    slug: 'psychology-mind-body',
    emoji: '💭',
    active: 'from-emerald-500 to-teal-600',
    ring: 'ring-emerald-200',
    badge: 'from-emerald-500 to-teal-600',
    blurb: 'Behaviour, emotion and mind-body practice',
    heading: 'Psychology and mind-body practice',
    intro: 'Self-trust, emotional regulation, fear of movement, breathwork and the practices that act on the nervous system rather than talking around it. Mechanism first, technique second.',
    metaTitle: 'Psychology & Mind-Body Articles for Chronic Illness | Recalibrate',
    metaDescription: 'The psychology of long-term illness: self-trust as a prediction, emotional regulation, catastrophising and fear of movement, plus mind-body practices with real mechanisms behind them.',
  },
  {
    name: 'Self-Management',
    slug: 'self-management',
    emoji: '🌱',
    active: 'from-amber-500 to-orange-600',
    ring: 'ring-amber-200',
    badge: 'from-amber-500 to-orange-600',
    blurb: 'Pacing, tracking, assessment and daily strategy',
    heading: 'Self-management that survives a bad week',
    intro: 'Pacing, tracking, self-assessment and the boring structural changes that actually hold up in a flare. How to gather data on yourself and read it without turning your life into a spreadsheet.',
    metaTitle: 'Self-Management Articles - Pacing & Symptom Tracking | Recalibrate',
    metaDescription: 'Practical self-management for chronic pain and chronic illness: pacing under your threshold, breaking the boom-bust cycle, symptom tracking, self-assessment and measuring allostatic load.',
  },
  {
    name: 'Lifestyle and Wellness',
    slug: 'lifestyle-and-wellness',
    emoji: '🌿',
    active: 'from-lime-500 to-green-600',
    ring: 'ring-lime-200',
    badge: 'from-lime-500 to-green-600',
    blurb: 'Sleep, nutrition, movement and recovery',
    heading: 'Sleep, food, movement, recovery',
    intro: 'The inputs you control every day and what the evidence actually shows about them - blood sugar and cortisol, sleep architecture, natural analgesics, movement dosing and hydration. No wellness slogans.',
    metaTitle: 'Sleep, Nutrition & Recovery Articles | Recalibrate',
    metaDescription: 'Evidence-led lifestyle articles: cortisol and blood sugar, sleep and pain thresholds, natural painkillers and the compounds inside them, movement dosing, and recovery that does not cost you tomorrow.',
  },
  {
    name: 'Treatment Options',
    slug: 'treatment-options',
    emoji: '💊',
    active: 'from-sky-500 to-blue-600',
    ring: 'ring-sky-200',
    badge: 'from-sky-500 to-blue-600',
    blurb: 'What the evidence says about interventions',
    heading: 'Treatments, and what the evidence says',
    intro: 'Medications, procedures, manual therapy and the alternatives - what each one is actually doing mechanistically, what the trials show, and what the honest limitations are.',
    metaTitle: 'Chronic Pain Treatment Options - Evidence Reviews | Recalibrate',
    metaDescription: 'What the evidence says about chronic pain and chronic illness treatments: medications, manual therapy, injections, supplements and emerging options - mechanisms, trial data and honest limitations.',
  },
  {
    name: 'Care and Support',
    slug: 'care-and-support',
    emoji: '🤝',
    active: 'from-violet-500 to-fuchsia-600',
    ring: 'ring-violet-200',
    badge: 'from-violet-500 to-fuchsia-600',
    blurb: 'Carers, advocacy and being believed',
    heading: 'Being believed, and being supported',
    intro: 'Appointments, advocacy, carers, family and the social side of long-term illness - including how to bring people into your data instead of trying to describe six years in fifteen minutes.',
    metaTitle: 'Care, Support & Advocacy for Chronic Illness | Recalibrate',
    metaDescription: 'Advocacy and support for people with chronic pain and chronic illness: preparing for appointments, being believed, involving carers and family, and building a care team around your own data.',
  },
  {
    name: 'Clinician Tools',
    slug: 'clinician-tools',
    emoji: '🩺',
    active: 'from-cyan-600 to-sky-700',
    ring: 'ring-cyan-200',
    badge: 'from-cyan-600 to-sky-700',
    blurb: 'For practitioners working with complex patients',
    heading: 'For clinicians and allied health',
    intro: 'Written for practitioners: assessment approaches, between-appointment data, explaining mechanisms to patients, and working with complex multi-system presentations.',
    metaTitle: 'Clinician Tools & Resources for Complex Patients | Recalibrate',
    metaDescription: 'Resources for physiotherapists, psychologists, GPs and allied health working with chronic pain and complex chronic illness: assessment, patient data between appointments and explaining mechanisms well.',
  },
  {
    name: 'Digital Health',
    slug: 'digital-health',
    emoji: '📱',
    active: 'from-blue-500 to-indigo-600',
    ring: 'ring-blue-200',
    badge: 'from-blue-500 to-indigo-600',
    blurb: 'Apps, AI and connected care',
    heading: 'Digital health, AI and connected care',
    intro: 'Where technology genuinely helps in long-term health - tracking that surfaces patterns, AI that reads your own data, and care that stays connected between appointments.',
    metaTitle: 'Digital Health, Health Apps & AI Articles | Recalibrate',
    metaDescription: 'Digital health for chronic conditions: symptom tracking apps, AI health insights from your own data, connected care teams, and what to look for in a health app that is actually useful.',
  },
];

// Retired categories → their canonical home.
const LEGACY_CATEGORY_MAP = {
  'Nervous System': 'Neuroscience',
  'Physiology': 'Neuroscience',
  'Biochemistry': 'Neuroscience',
  'Psychology': 'Psychology & Mind-Body',
  'Mind-Body Techniques': 'Psychology & Mind-Body',
  'Mental Health': 'Psychology & Mind-Body',
  'Tracker': 'Self-Management',
  'Self-Assessment': 'Self-Management',
  'Measurement': 'Self-Management',
  'Nutrition': 'Lifestyle and Wellness',
  'Sleep': 'Lifestyle and Wellness',
  'Movement': 'Lifestyle and Wellness',
  'Patient Advocacy': 'Care and Support',
  'Condition Guides': 'Pain Science',
  'LinkedIn Essays': 'Neuroscience',
};

export const CATEGORY_ORDER = BLOG_CATEGORIES.map((c) => c.name);

export const CATEGORY_STYLE = BLOG_CATEGORIES.reduce((acc, c) => {
  acc[c.name] = c;
  return acc;
}, {});

export const DEFAULT_CATEGORY_STYLE = {
  name: 'Neuroscience',
  emoji: '📖',
  active: 'from-gray-700 to-gray-900',
  ring: 'ring-gray-200',
  badge: 'from-purple-500 to-indigo-600',
};

export const normaliseCategory = (category) => {
  if (!category) return 'Neuroscience';
  if (CATEGORY_STYLE[category]) return category;
  // Anything unmapped lands in the general bucket rather than silently adding a
  // tenth category to the dropdown.
  return LEGACY_CATEGORY_MAP[category] || 'Lifestyle and Wellness';
};

export const categoryStyle = (category) =>
  CATEGORY_STYLE[normaliseCategory(category)] || DEFAULT_CATEGORY_STYLE;

export const CATEGORY_BY_SLUG = BLOG_CATEGORIES.reduce((acc, c) => {
  acc[c.slug] = c;
  return acc;
}, {});

export const categorySlug = (category) => {
  const entry = CATEGORY_STYLE[normaliseCategory(category)];
  return entry ? entry.slug : 'neuroscience';
};
